/**
 * Format utilities for consistent display of data
 */

/**
 * Format a number with commas as thousands separators and optional decimal places
 * @param {number} number - The number to format
 * @param {number} [decimals=0] - Number of decimal places
 * @returns {string} Formatted number
 */
export const formatNumber = (number, decimals = 0) => {
  if (number === null || number === undefined) return '0'

  const num = typeof number === 'string' ? parseFloat(number) : number

  if (isNaN(num)) return '0'

  return num.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  })
}

/**
 * Format minutes to hours and minutes
 * @param {number} minutes - Total minutes
 * @returns {string} Formatted time string (e.g., "2h 30m")
 */
export const formatMinutes = (minutes) => {
  if (!minutes) return '0m'

  const hours = Math.floor(minutes / 60)
  const remainingMinutes = Math.round(minutes % 60)

  if (hours === 0) {
    return `${remainingMinutes}m`
  } else if (remainingMinutes === 0) {
    return `${hours}h`
  } else {
    return `${hours}h ${remainingMinutes}m`
  }
}

/**
 * Format a percentage for display
 * @param {number} value - The percentage value (0-100)
 * @param {number} [decimals=0] - Number of decimal places
 * @returns {string} Formatted percentage
 */
export const formatPercentage = (value, decimals = 0) => {
  if (value === null || value === undefined) return '0%'

  const num = typeof value === 'string' ? parseFloat(value) : value

  if (isNaN(num)) return '0%'

  return `${num.toFixed(decimals)}%`
}

/**
 * Format a date as a date-only string (YYYY-MM-DD)
 * For input fields like date pickers
 * @param {Date} date - Date to format
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const formatDateForInput = (date) => {
  return date ? date.toISOString().slice(0, 10) : ''
}

/**
 * Format a date with the specified display format
 * Uses UTC to preserve the raw database timestamp without browser timezone conversion
 * Database stores timestamps in Eastern Time as "timestamp without time zone"
 * @param {Date|string} date - Date to format
 * @param {string} format - Format to use (full, dateTime, date, month, time)
 * @returns {string} Formatted date string
 */
export const formatDate = (date, format = 'date') => {
  if (!date) return ''

  const dateObj = typeof date === 'string' ? new Date(date) : date

  const timezone = 'UTC'

  const formatOptions = {
    full: {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: timezone
    },
    dateTime: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZone: timezone
    },
    date: {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: timezone
    },
    month: {
      year: 'numeric',
      month: 'long',
      timeZone: timezone
    },
    time: {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: timezone
    }
  }

  return dateObj.toLocaleDateString('en-US', formatOptions[format] || formatOptions.date)
}

/**
 * Format a data value for display based on its type
 * @param {number|string|Date} value - The value to format
 * @param {string} type - Type of formatting to apply (number, percentage, minutes, date, etc)
 * @param {Object} options - Additional formatting options
 * @returns {string} Formatted value
 */
export const formatValue = (value, type, options = {}) => {
  const { decimals = 0, format = 'date' } = options

  switch (type) {
    case 'number':
      return formatNumber(value, decimals)
    case 'percentage':
      return formatPercentage(value, decimals)
    case 'minutes':
      return formatMinutes(value)
    case 'date':
      return formatDate(value, format)
    case 'dateInput':
      return formatDateForInput(value)
    default:
      return value !== null && value !== undefined ? value.toString() : ''
  }
}

/**
 * Format seconds into HH:mm:ss string
 * @param {number} seconds - Duration in seconds
 * @returns {string} Formatted time string (e.g., "01:30:00")
 */
export const formatHandleTimeSeconds = (seconds) => {
  if (!seconds || seconds === 0) return '00:00:00'
  const hrs = Math.floor(seconds / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60
  return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
}
